import { useAuth } from "@elit-hair/elit-tools";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { callSvcAxiosInstance } from "@/constants";
import { callServiceEndpoints } from "@/constants/endpoints";
import { QueryKeyType, createQueryKey } from "@/constants/query-keys";
import {
    ApiPhoneNumber,
    AssignPhoneNumberPayload,
    AvailablePhoneNumber,
    CreatePhoneNumberPayload,
    IPhoneNumber,
    PhoneNumberStatus,
    UnassignPhoneNumberPayload,
} from "@/types/phone.types";

const mapApiPhoneNumbers = (data: ApiPhoneNumber[]): IPhoneNumber[] => {
    if (!Array.isArray(data)) {
        console.error("API response is not an array");
        return [];
    }

    return data
        .map((item: ApiPhoneNumber) => {
            if (!item || typeof item !== "object") {
                return null;
            }

            try {
                const phoneNumber: IPhoneNumber = {
                    id: item.id || "",
                    number: item.phoneNumber,
                    isActive: item.status === PhoneNumberStatus.ACTIVE,
                    name: item.name,
                    countries: item.countries,
                    brands: item.brands,
                    priority: item.priority,
                };

                return phoneNumber;
            } catch (err) {
                console.error(`Error processing phone number:`, err);
                return null;
            }
        })
        .filter((item): item is IPhoneNumber => item !== null);
};

const getPhoneNumbers = async (): Promise<IPhoneNumber[]> => {
    const response = await callSvcAxiosInstance.get(callServiceEndpoints.getPhoneNumbers);
    return mapApiPhoneNumbers(response.data);
};

const getMyPhoneNumbers = async (): Promise<IPhoneNumber[]> => {
    const response = await callSvcAxiosInstance.get(callServiceEndpoints.getMyPhoneNumbers);
    return mapApiPhoneNumbers(response.data);
};

const getPhoneNumbersByUserId = async (userId: string): Promise<IPhoneNumber[]> => {
    const response = await callSvcAxiosInstance.get(callServiceEndpoints.getPhoneNumbersByUserId(userId));
    return mapApiPhoneNumbers(response.data);
};

const getPhoneNumberUsers = async (phoneNumberId: string): Promise<string[]> => {
    const response = await callSvcAxiosInstance.get(callServiceEndpoints.getPhoneNumberUsers(phoneNumberId));

    if (!Array.isArray(response.data)) {
        console.error("API response is not an array");
        return [];
    }

    return response.data.filter((userId): userId is string => typeof userId === "string");
};

const getAvailablePhoneNumbers = async (): Promise<AvailablePhoneNumber[]> => {
    const response = await callSvcAxiosInstance.get(callServiceEndpoints.getAvailablePhoneNumbers);

    if (!Array.isArray(response.data)) {
        console.error("API response is not an array");
        return [];
    }

    return response.data;
};

const createPhoneNumber = async (data: CreatePhoneNumberPayload): Promise<ApiPhoneNumber> => {
    const response = await callSvcAxiosInstance.post(callServiceEndpoints.createPhoneNumber, data);
    return response.data;
};

const updatePhoneNumber = async ({
    id,
    data,
}: {
    id: string;
    data: CreatePhoneNumberPayload;
}): Promise<ApiPhoneNumber> => {
    const response = await callSvcAxiosInstance.patch(callServiceEndpoints.updatePhoneNumber(id), data);
    return response.data;
};

const deletePhoneNumber = async (id: string): Promise<void> => {
    await callSvcAxiosInstance.delete(callServiceEndpoints.deletePhoneNumber(id));
};

const assignPhoneNumber = async (payload: AssignPhoneNumberPayload): Promise<void> => {
    await callSvcAxiosInstance.post(callServiceEndpoints.assignPhoneNumberAccess, payload);
};

const unassignPhoneNumber = async (payload: UnassignPhoneNumberPayload): Promise<void> => {
    await callSvcAxiosInstance.delete(callServiceEndpoints.unassignPhoneNumberAccess, {
        params: payload,
    });
};

export const usePhoneNumbers = () => {
    const { auth } = useAuth();

    return useQuery({
        queryKey: createQueryKey.phoneNumbers(),
        queryFn: getPhoneNumbers,
        enabled: !!auth, // Only run when authenticated
    });
};

export const useMyPhoneNumbers = () => {
    const { auth } = useAuth();

    return useQuery({
        queryKey: createQueryKey.myPhoneNumbers(),
        queryFn: getMyPhoneNumbers,
        enabled: !!auth,
    });
};

export const usePhoneNumbersByUserId = (userId: string | undefined, enabled = true) => {
    const { auth } = useAuth();

    return useQuery({
        queryKey: createQueryKey.phoneNumbersByUserId(userId ?? ""),
        queryFn: () => {
            if (!userId) {
                return Promise.resolve([]);
            }
            return getPhoneNumbersByUserId(userId);
        },
        enabled: !!auth && !!userId && enabled,
    });
};

export const usePhoneNumberUsers = (phoneNumberId: string | undefined, enabled = true) => {
    const { auth } = useAuth();

    return useQuery({
        queryKey: createQueryKey.phoneNumberUsers(phoneNumberId ?? ""),
        queryFn: () => {
            if (!phoneNumberId) {
                return Promise.resolve([]);
            }
            return getPhoneNumberUsers(phoneNumberId);
        },
        enabled: !!auth && !!phoneNumberId && enabled,
    });
};

export const usePhoneNumberUsersForIds = (phoneNumberIds: string[], enabled = true) => {
    const { auth } = useAuth();

    return useQueries({
        queries: phoneNumberIds.map((phoneNumberId) => ({
            queryKey: createQueryKey.phoneNumberUsers(phoneNumberId),
            queryFn: () => getPhoneNumberUsers(phoneNumberId),
            enabled: !!auth && !!phoneNumberId && enabled,
        })),
    });
};

export const useAvailablePhoneNumbers = () => {
    const { auth } = useAuth();

    return useQuery({
        queryKey: createQueryKey.availablePhoneNumbers(),
        queryFn: getAvailablePhoneNumbers,
        enabled: !!auth, // Only run when authenticated
    });
};

const invalidatePhoneNumberQueries = (
    queryClient: ReturnType<typeof useQueryClient>,
    options?: {
        phoneNumberId?: string;
        userId?: string;
    }
) => {
    queryClient.invalidateQueries({ queryKey: createQueryKey.phoneNumbers() });
    queryClient.invalidateQueries({ queryKey: createQueryKey.myPhoneNumbers() });

    if (options?.phoneNumberId) {
        queryClient.invalidateQueries({ queryKey: createQueryKey.phoneNumberUsers(options.phoneNumberId) });
    } else {
        queryClient.invalidateQueries({ queryKey: [QueryKeyType.PHONE_NUMBER_USERS] });
    }

    if (options?.userId) {
        queryClient.invalidateQueries({ queryKey: createQueryKey.phoneNumbersByUserId(options.userId) });
    } else {
        queryClient.invalidateQueries({ queryKey: [QueryKeyType.PHONE_NUMBERS_BY_USER] });
    }
};

export const useCreatePhoneNumber = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createPhoneNumber,
        onSuccess: () => {
            invalidatePhoneNumberQueries(queryClient);
        },
    });
};

export const useUpdatePhoneNumber = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updatePhoneNumber,
        onSuccess: () => {
            invalidatePhoneNumberQueries(queryClient);
        },
    });
};

export const useDeletePhoneNumber = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deletePhoneNumber,
        onSuccess: () => {
            invalidatePhoneNumberQueries(queryClient);
        },
    });
};

export const useAssignPhoneNumber = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: assignPhoneNumber,
        onSuccess: (_data, variables) => {
            invalidatePhoneNumberQueries(queryClient, {
                phoneNumberId: variables.phoneNumberId,
                userId: variables.userId,
            });
        },
    });
};

export const useUnassignPhoneNumber = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: unassignPhoneNumber,
        onSuccess: (_data, variables) => {
            invalidatePhoneNumberQueries(queryClient, {
                phoneNumberId: variables.phoneNumberId,
                userId: variables.userId,
            });
        },
    });
};
